import { NavLink } from 'react-router-dom';
import { db } from '../db/indexedDb';
import { useSettings } from '../hooks/useIndexedDb';
import { cn } from '../lib/cn';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current stroke-[1.8]">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.1M12 19.1v2.1M21.2 12h-2.1M4.9 12H2.8M18.5 5.5 17 7M7 17l-1.5 1.5M18.5 18.5 17 17M7 7 5.5 5.5" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
      <path d="M14.8 2.6a8.8 8.8 0 1 0 6.6 14.7 8 8 0 1 1-6.6-14.7Z" />
    </svg>
  );
}

const navItems = [
  { href: '/', label: 'Home' },
  { href: '/upload', label: 'Upload' },
  { href: '/songs', label: 'All Songs' },
  { href: '/playlists', label: 'Playlists' },
  { href: '/now-playing', label: 'Now Playing' }
];

export function TopBar({ currentPath }: { currentPath: string }) {
  const settings = useSettings();

  const toggleTheme = async () => {
    const nextTheme = settings?.theme === 'light' ? 'dark' : 'light';
    await db.updateSettings({ theme: nextTheme });
    document.documentElement.classList.toggle('light', nextTheme === 'light');
    document.documentElement.classList.toggle('dark', nextTheme !== 'light');
  };

  return (
    <header className="sticky top-2 z-30 rounded-[1rem] border border-white/8 bg-card/72 px-3 py-2 backdrop-blur-xl sm:top-4 sm:rounded-[2rem] sm:border-white/10 sm:px-6 sm:py-4">
      <div className="flex flex-col gap-2 sm:gap-2.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="hidden text-xs uppercase tracking-[0.35em] text-accent sm:block">
              Offline-first PWA
            </p>
            <h1 className="text-[0.98rem] font-semibold tracking-[-0.04em] [text-rendering:optimizeLegibility] sm:mt-2 sm:text-[2rem]">
              Local Wave
            </h1>
            <p className="hidden text-sm text-foreground/70 sm:mt-1 sm:block">
              Upload MP3s, keep them on-device, and play them anywhere.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void toggleTheme()}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground/80 transition hover:bg-white/10 sm:hidden"
            aria-label={`Switch to ${settings?.theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Theme: ${settings?.theme ?? 'dark'}`}
          >
            {settings?.theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </button>
        </div>
        <div className="flex items-start gap-3 sm:items-center">
          <nav className="flex min-w-0 flex-1 flex-wrap gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium transition sm:px-4 sm:py-2 sm:text-sm',
                    isActive || currentPath === item.href
                      ? 'bg-accent text-black'
                      : 'bg-white/5 text-foreground/80 hover:bg-white/10'
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => void toggleTheme()}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground/80 transition hover:bg-white/10 sm:inline-flex"
            aria-label={`Switch to ${settings?.theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Theme: ${settings?.theme ?? 'dark'}`}
          >
            {settings?.theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </button>
        </div>
      </div>
    </header>
  );
}
